import { useMemo } from "react";
import {
  widgetViewPolicy,
  type WidgetViewTier,
  type WidgetViewWriteGrant,
} from "#app/widgets/dashboards/widget-view-policy";
import { useAppData } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { usePermission } from "#app/lib/hooks/use-permission";
import { scopeEntityId, scopeKey, type IssueScope } from "#app/lib/filters/issue-scope";

/**
 * The client half of the widget view-ownership model. The decision itself is
 * `widgetViewPolicy` (pure, over the SAME shared grammar the server guard
 * feeds), so the edit affordances shown here and what the API will actually
 * accept can't drift — the notes `viewerCanEdit` precedent. This hook only
 * resolves the actor inputs from client data: the `widgets.manage`
 * permission, the current user id (owner compare — `/my-work` always renders
 * the current user), and team leadership from the workspace `teamMap`.
 */

export type { WidgetViewTier };

// The dashboard's view keys — persisted VALUES ("widget-view-workflow:<id>",
// "widget-view-member:<id>", …); renaming them orphans existing dashboards.
// Optional variants suffix the key so two views can share one scope (a team's
// Overview vs its Dashboard tab).
export function viewFor(scope: IssueScope, variant?: string): string {
  const base = `widget-view-${scopeKey(scope)}`;
  return variant ? `${base}-${variant}` : base;
}

export type WidgetViewAccessState = {
  /** The persisted view key — `viewFor(scope, variant)`. */
  view: string;
  /** Whether writes to the canonical board will be accepted by the server. */
  canWrite: boolean;
  /** WHICH grant made the viewer a shared-board editor ("team-lead" |
   *  "permission") — copy-level context for the ownership pill; null for
   *  non-editors. */
  writeGrant: WidgetViewWriteGrant | null;
  tier: WidgetViewTier;
};

export function useWidgetViewAccess(
  scope: IssueScope,
  variant?: string,
): WidgetViewAccessState {
  const canManageWidgets = usePermission("widgets.manage");
  const { currentUser } = useAppData();
  const { teamMap } = useWorkspaceData();
  const userId = currentUser?.userId ?? "";

  return useMemo(() => {
    const view = viewFor(scope, variant);
    const policy = widgetViewPolicy(view, {
      userId,
      canManageWidgets,
      leadsTeam: (teamId) => teamMap.get(teamId)?.viewerRole === "lead",
    });
    return { view, ...policy };
    // Memoize on primitives (hosts re-create the scope object per render —
    // the dashboard's entityScope memo precedent).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeEntityId(scope), variant, userId, canManageWidgets, teamMap]);
}
